/**
 * AgentWorkforce — the "AI Agent Workforce" control surface.
 *
 * Sits above the Assistant in the AI Asset Manager module. Where the Assistant
 * is a single conversational agent, this is the operations console for the
 * *fleet*: six specialist workers, each of which runs one agent per playbook
 * you switch on. The page is built as an app surface, not a deck —
 *
 *   • a compact operational header with live run totals,
 *   • a fleet tab strip, each tab carrying a live running-count indicator,
 *   • a per-fleet console: an autonomy dial plus per-playbook deploy toggles.
 *
 * The running count is NOT an arbitrary replica number — it is the number of
 * playbooks (scenarios) currently deployed in that fleet. Deploy the
 * "Underperformance diagnosis" and "Availability guarantee" playbooks in the
 * Technical fleet and that fleet shows 2 agents running. That is the unit of
 * scale and it maps 1:1 to what's on screen.
 *
 * UI-only. Everything here is local component state — there is no backend wired
 * yet (hence the "preview" tag). Brand: cream/ink ground, Signal green reserved
 * for the active accent. All colour comes from theme tokens so light/dark work.
 */

import React, { useMemo, useState } from 'react';
import {
  Box, Stack, Typography, Chip, Tabs, Tab, Slider, Tooltip, Button, Switch,
} from '@mui/material';

import RadarOutlinedIcon from '@mui/icons-material/RadarOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import ShowChartOutlinedIcon from '@mui/icons-material/ShowChartOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';

import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import LanOutlinedIcon from '@mui/icons-material/LanOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import NorthEastRoundedIcon from '@mui/icons-material/NorthEastRounded';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';

const MONO = '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace';

// Signal green as rgba so we can tint at low alpha without pulling the hex apart.
const sig = (a) => `rgba(30, 155, 73, ${a})`;

// Live pulse used by the running dots — defined once, reused.
const PULSE = {
  animation: 'wf-live 1.8s ease-in-out infinite',
  '@keyframes wf-live': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.35 } },
};

// ── Autonomy model ────────────────────────────────────────────────────────────
const AUTONOMY = [
  { id: 'suggest',  label: 'Suggest',  blurb: 'Surfaces findings and drafts. You act.' },
  { id: 'copilot',  label: 'Co-pilot', blurb: 'Prepares the action; you approve each one.' },
  { id: 'autopilot',label: 'Autopilot',blurb: 'Acts within guardrails; escalates the rest.' },
];

// Guardrail tone for an individual scenario — its inherent risk.
const MODE = {
  notify:  { label: 'Notify only',    tone: 'neutral' },
  approve: { label: 'Needs approval', tone: 'warn'    },
  auto:    { label: 'Runs unattended',tone: 'signal'  },
};

// ── The fleet ───────────────────────────────────────────────────────────────
const WORKERS = [
  {
    id: 'market',
    code: 'W1',
    name: 'Market Intelligence & Data',
    short: 'Market Intelligence',
    icon: RadarOutlinedIcon,
    tagline: 'An always-on radar across every NEM region.',
    mission:
      'Ingests dispatch, price, demand and FCAS signals across all five NEM ' +
      'regions in real time. Surfaces the moves that matter before they reach ' +
      'your P&L.',
    integrations: 6,
    capabilities: [
      '5-minute price & dispatch', 'Pre-dispatch & ST-PASA', 'FCAS co-optimisation',
      'Negative-price alerts', 'Interconnector flows', 'Curtailment watch',
    ],
    dataSources: ['AEMO NEMWEB (5-min)', 'Pre-dispatch / ST-PASA', 'LGC spot', 'BoM weather'],
    actions: ['Raise alert', 'Annotate dashboard', 'Hand off to Commercial worker'],
    scenarios: [
      {
        name: 'Negative-price event forming',
        trigger: 'Pre-dispatch RRP < $0 in your region for 2+ intervals',
        steps: [
          'Pull pre-dispatch and ST-PASA for the affected region',
          'Cross-check your assets’ current bid stack and ramp limits',
          'Estimate curtailment exposure in MWh and $',
        ],
        output: 'Alert with the exposure window and a suggested bid response',
        mode: 'notify',
      },
      {
        name: 'Price-spike capture window',
        trigger: 'Pre-dispatch RRP > $300/MWh inside the next 6 intervals',
        steps: [
          'Confirm available capacity and battery state-of-charge',
          'Size the dispatchable energy for the spike window',
          'Draft the bid / discharge recommendation',
        ],
        output: 'Capture plan handed to the Commercial worker for approval',
        mode: 'approve',
      },
      {
        name: 'Forecast drift detected',
        trigger: 'UIGF vs actual generation deviates > 10% for an asset',
        steps: [
          'Quantify the drift and its revenue sensitivity',
          'Tag the likely cause (weather, cloud, soiling, clipping)',
          'Notify the Technical worker for diagnosis',
        ],
        output: 'Forecast-quality flag with a routed diagnostic ticket',
        mode: 'auto',
      },
    ],
    feed: [
      { text: 'Flagged negative-price risk in QLD1 16:30–17:00', tone: 'warn',   t: '2m ago' },
      { text: 'Logged FCAS regulation co-optimisation in SA1',    tone: 'neutral',t: '24m ago' },
      { text: 'No interconnector binding constraints in last 4h', tone: 'ok',     t: '1h ago' },
    ],
  },
  {
    id: 'contract',
    code: 'W2',
    name: 'Contract & Offtake Management',
    short: 'Contract & Offtake',
    icon: HandshakeOutlinedIcon,
    tagline: 'Every PPA obligation, tracked and reconciled.',
    mission:
      'Reads your contracts, tracks every obligation and settlement window, and ' +
      'reconciles offtake against generation. No notice, cap or floor goes missed.',
    integrations: 5,
    capabilities: [
      'PPA term extraction', 'Obligation calendar', 'Settlement reconciliation',
      'Strike / cap / floor watch', 'LGC delivery tracking', 'Invoice matching',
    ],
    dataSources: ['Contract documents', 'Generation actuals', 'RRP', 'LGC registry', 'Invoices'],
    actions: ['Draft settlement statement', 'Raise obligation reminder', 'Flag variance'],
    scenarios: [
      {
        name: 'Monthly settlement reconciliation',
        trigger: 'Settlement window opens for a contracted asset',
        steps: [
          'Reconcile metered generation against the PPA volume',
          'Compute the payment and the price applied (strike / floating)',
          'Flag any variance beyond tolerance with its driver',
        ],
        output: 'Draft settlement statement ready for review before counterparty send',
        mode: 'approve',
      },
      {
        name: 'Contractual notice due',
        trigger: 'A reporting or notice obligation falls due within 7 days',
        steps: [
          'Assemble the figures and exhibits the notice requires',
          'Draft the notice against the contract template',
          'Stage it for sign-off ahead of the deadline',
        ],
        output: 'Drafted notice with the deadline and recipient pre-filled',
        mode: 'approve',
      },
      {
        name: 'Collar breach',
        trigger: 'Realised price moves outside the contract cap or floor',
        steps: [
          'Compute the hedge settlement impact for the period',
          'Attribute it to the breaching intervals',
          'Notify the Commercial worker for the revenue view',
        ],
        output: 'Collar-settlement note routed to Commercial',
        mode: 'auto',
      },
    ],
    feed: [
      { text: 'Reconciled May offtake — variance 0.4%, within tolerance', tone: 'ok', t: '12m ago' },
      { text: 'Drafted quarterly availability notice (due in 5 days)',    tone: 'warn', t: '3h ago' },
      { text: 'Matched 14 invoices to delivered LGCs',                    tone: 'neutral', t: '1d ago' },
    ],
  },
  {
    id: 'technical',
    code: 'W3',
    name: 'Technical Asset Management',
    short: 'Technical Mgmt',
    icon: MonitorHeartOutlinedIcon,
    tagline: 'Eyes on every inverter — with diagnostics.',
    mission:
      'Watches SCADA and availability across the fleet, runs diagnostics on ' +
      'underperformance, and ranks the work orders that recover the most MWh.',
    integrations: 5,
    capabilities: [
      'SCADA anomaly detection', 'Availability tracking', 'Underperformance diagnosis',
      'Degradation analysis', 'Outage impact', 'Work-order ranking',
    ],
    dataSources: ['SCADA telemetry', 'Availability / outages', 'UIGF', 'Weather', 'OEM specs'],
    actions: ['Open work order', 'Notify O&M', 'Project LD exposure'],
    scenarios: [
      {
        name: 'Underperformance diagnosis',
        trigger: 'Actual vs expected MW gap exceeds threshold for an asset',
        steps: [
          'Rule out curtailment, weather and clipping as the cause',
          'Isolate the string / inverter / tracker behind the loss',
          'Classify the fault and quantify the MWh and $ at stake',
        ],
        output: 'Ranked work order with lost-revenue estimate, routed to O&M',
        mode: 'notify',
      },
      {
        name: 'Availability guarantee at risk',
        trigger: 'Rolling availability approaches the contractual threshold',
        steps: [
          'Recompute energy-based availability against the guarantee',
          'Project the liquidated-damages exposure for the term',
          'Surface the assets dragging the rolling figure',
        ],
        output: 'Availability-risk alert with projected LD exposure',
        mode: 'approve',
      },
      {
        name: 'Planned-outage scheduling',
        trigger: 'An O&M outage is requested for a window',
        steps: [
          'Model MWh and revenue at risk across candidate windows',
          'Weight by forecast irradiance / wind and price',
          'Recommend the lowest-impact slot',
        ],
        output: 'Outage-window recommendation with revenue-at-risk per option',
        mode: 'auto',
      },
    ],
    feed: [
      { text: 'Inverter 7 underperforming — 2.1% loss, work order raised', tone: 'warn', t: '8m ago' },
      { text: 'Fleet availability 99.3% (7-day rolling)',                  tone: 'ok',   t: '40m ago' },
      { text: 'Cleared curtailment as cause for SA array dip',             tone: 'neutral', t: '2h ago' },
    ],
  },
  {
    id: 'commercial',
    code: 'W4',
    name: 'Commercial Asset Management',
    short: 'Commercial Mgmt',
    icon: ShowChartOutlinedIcon,
    tagline: 'Turns generation into optimised revenue.',
    mission:
      'Owns capture price, dispatch strategy and revenue performance. Connects ' +
      'what the asset can physically do to what the market will actually pay.',
    integrations: 5,
    capabilities: [
      'Capture-price analysis', 'Dispatch optimisation', 'Revenue attribution',
      'Curtailment economics', 'Bid-strategy review', 'Merchant vs contracted mix',
    ],
    dataSources: ['Dispatch', 'RRP', 'Generation', 'Contracts', 'Bid stack'],
    actions: ['Build variance waterfall', 'Recommend bid change', 'Brief Portfolio worker'],
    scenarios: [
      {
        name: 'Revenue variance attribution',
        trigger: 'Month-to-date revenue diverges from budget',
        steps: [
          'Decompose the gap into price, volume, curtailment and availability',
          'Tie each component back to its source intervals',
          'Write the plain-language narrative for the report',
        ],
        output: 'Variance waterfall with a written attribution',
        mode: 'notify',
      },
      {
        name: 'Curtailment economics',
        trigger: 'Economic vs network curtailment classified on an asset',
        steps: [
          'Separate economic (bid > RRP) from network curtailment',
          'Quantify the $ left on the table for each',
          'Recommend a bid response where economic',
        ],
        output: 'Curtailment ledger with a suggested bid adjustment',
        mode: 'approve',
      },
      {
        name: 'Capture-price erosion',
        trigger: 'Capture ratio falls versus the peer set',
        steps: [
          'Test for timing, cannibalisation and clipping effects',
          'Benchmark against comparable assets in the region',
          'Propose a dispatch or hedging adjustment',
        ],
        output: 'Capture-erosion diagnosis with a strategy recommendation',
        mode: 'auto',
      },
    ],
    feed: [
      { text: 'June capture ratio 84% vs 81% peer median', tone: 'ok', t: '15m ago' },
      { text: 'Attributed $42k MTD variance to weak volume', tone: 'warn', t: '1h ago' },
      { text: 'Flagged $9k economic curtailment in NSW1', tone: 'neutral', t: '5h ago' },
    ],
  },
  {
    id: 'financial',
    code: 'W5',
    name: 'Financial Reporting',
    short: 'Financial Reporting',
    icon: AssessmentOutlinedIcon,
    tagline: 'Board-ready numbers, on demand.',
    mission:
      'Assembles revenue, cost and covenant reporting from source data. Closes ' +
      'the month and answers the lender before they ask.',
    integrations: 5,
    capabilities: [
      'Revenue & cost roll-up', 'DSCR / ICR computation', 'Budget vs actual',
      'Investor reporting packs', 'Covenant headroom', 'Audit trail',
    ],
    dataSources: ['Generation', 'Contracts', 'Invoices', 'Debt schedule', 'Opex ledger'],
    actions: ['Assemble reporting pack', 'Compute covenant ratios', 'Draft lender response'],
    scenarios: [
      {
        name: 'Month-end close',
        trigger: 'A reporting period closes',
        steps: [
          'Roll up revenue, opex and debt service for the period',
          'Compute DSCR, ICR and the budget-vs-actual bridge',
          'Assemble the pack with every figure sourced',
        ],
        output: 'Draft reporting pack ready for CFO review',
        mode: 'approve',
      },
      {
        name: 'Covenant test approaching',
        trigger: 'A covenant test date falls within the window',
        steps: [
          'Compute the tested ratios on locked figures',
          'Project headroom against the covenant level',
          'Flag any breach risk early with its driver',
        ],
        output: 'Covenant test sheet with projected headroom',
        mode: 'approve',
      },
      {
        name: 'Ad-hoc lender query',
        trigger: 'A lender or investor requests a figure',
        steps: [
          'Locate the source data behind the request',
          'Assemble the answer with citations to the workbook',
          'Draft the response in the house format',
        ],
        output: 'Cited draft response to the query',
        mode: 'auto',
      },
    ],
    feed: [
      { text: 'Closed May — DSCR 1.42x, 0.12x headroom', tone: 'ok', t: '30m ago' },
      { text: 'Drafted Q2 investor pack (12 pages)', tone: 'neutral', t: '4h ago' },
      { text: 'Covenant test due in 9 days — on track', tone: 'warn', t: '1d ago' },
    ],
  },
  {
    id: 'portfolio',
    code: 'W6',
    name: 'Portfolio Optimization',
    short: 'Portfolio Opt.',
    icon: TuneOutlinedIcon,
    tagline: 'The whole portfolio, working as one.',
    mission:
      'Looks across every asset and contract to point capital, hedges and ' +
      'attention where they earn the most. The strategist on top of the stack.',
    integrations: 6,
    capabilities: [
      'Capital allocation', 'Hedge / PPA structuring', 'Risk concentration',
      'Refinancing windows', 'Acquisition screening', 'Scenario & sensitivity',
    ],
    dataSources: ['All worker outputs', 'Forward curves', 'Debt terms', 'Contract book'],
    actions: ['Draft allocation memo', 'Recommend hedge', 'Model refinancing'],
    scenarios: [
      {
        name: 'Hedge optimisation',
        trigger: 'Open merchant exposure drifts beyond the risk budget',
        steps: [
          'Measure exposure against the mandate’s risk limit',
          'Size the PPA / hedge volume and tenor to close it',
          'Model the revenue-certainty trade-off',
        ],
        output: 'Hedge recommendation for the investment committee',
        mode: 'approve',
      },
      {
        name: 'Quarterly capital allocation',
        trigger: 'A capital-review cycle opens',
        steps: [
          'Rank assets by risk-adjusted return',
          'Surface underperformers and reinvestment candidates',
          'Draft the allocation rationale',
        ],
        output: 'Capital-allocation memo with a ranked asset table',
        mode: 'notify',
      },
      {
        name: 'Refinancing window',
        trigger: 'Rates and covenant headroom turn favourable',
        steps: [
          'Model the interest saving across refi structures',
          'Test covenant headroom under each',
          'Recommend the timing and structure',
        ],
        output: 'Refinancing brief with modelled savings',
        mode: 'auto',
      },
    ],
    feed: [
      { text: 'Merchant exposure 18% — within 25% mandate', tone: 'ok', t: '20m ago' },
      { text: 'Modelled refi: ~$310k/yr saving at current curve', tone: 'neutral', t: '6h ago' },
      { text: 'Ranked Q2 allocation — 2 assets flagged', tone: 'warn', t: '1d ago' },
    ],
  },
];

// Illustrative initial run state — some fleets running multiple workers, some
// idle, so the tab indicators read as a real operations board.
// A worker = one agent running one playbook. The deployed state holds, per
// fleet, the set of scenario indices currently switched on. Its size is the
// number of agents running in that fleet — the number you see on the tab.
const INITIAL_DEPLOYED = {
  market: [0, 1],      // negative-price watch + spike capture
  contract: [0],       // settlement reconciliation
  technical: [0, 1, 2],// all three diagnostics
  commercial: [0],     // revenue variance
  financial: [],       // idle
  portfolio: [],       // idle
};
const INITIAL_AUTONOMY = {
  market: 'copilot', contract: 'copilot', technical: 'suggest',
  commercial: 'copilot', financial: 'suggest', portfolio: 'suggest',
};


export default function AgentWorkforce() {
  const [tab, setTab] = useState(0);
  // deployed[fleetId] = array of active scenario indices.
  const [deployed, setDeployed] = useState(INITIAL_DEPLOYED);
  const [autonomy, setAutonomy] = useState(INITIAL_AUTONOMY);

  const worker = WORKERS[tab];
  const counts = useMemo(
    () => Object.fromEntries(WORKERS.map((w) => [w.id, (deployed[w.id] || []).length])),
    [deployed],
  );

  const totals = useMemo(() => {
    const totalRunning = Object.values(counts).reduce((a, b) => a + b, 0);
    const activeFleets = Object.values(counts).filter((n) => n > 0).length;
    // Agents running under an autopilot fleet.
    const autopilot = WORKERS.reduce(
      (sum, w) => sum + (autonomy[w.id] === 'autopilot' ? counts[w.id] : 0), 0);
    return { totalRunning, activeFleets, autopilot };
  }, [counts, autonomy]);

  const toggleScenario = (id, idx) => setDeployed((p) => {
    const cur = p[id] || [];
    return { ...p, [id]: cur.includes(idx) ? cur.filter((x) => x !== idx) : [...cur, idx] };
  });
  const deployAll = (id) =>
    setDeployed((p) => ({ ...p, [id]: WORKERS.find((w) => w.id === id).scenarios.map((_, i) => i) }));
  const pauseAll = (id) => setDeployed((p) => ({ ...p, [id]: [] }));
  const setLevel = (id, lvl) => setAutonomy((p) => ({ ...p, [id]: lvl }));

  return (
    <Box sx={{ maxWidth: 1280, mx: 'auto', pb: 6 }}>
      <Header totals={totals} />
      <FleetTabBar tab={tab} onTab={setTab} counts={counts} />
      <FleetConsole
        worker={worker}
        deployedIdx={deployed[worker.id] || []}
        onToggleScenario={(idx) => toggleScenario(worker.id, idx)}
        onDeployAll={() => deployAll(worker.id)}
        onPauseAll={() => pauseAll(worker.id)}
        level={autonomy[worker.id]}
        onLevel={(lvl) => setLevel(worker.id, lvl)}
      />
    </Box>
  );
}


// ── Header ────────────────────────────────────────────────────────────────────
function Header({ totals }) {
  return (
    <Box sx={{ pt: { xs: 1, sm: 1.5 }, pb: 2.5 }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between"
             sx={{ flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.75 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main' }} />
            <Typography sx={{
              fontFamily: MONO, fontSize: '0.72rem', letterSpacing: '0.08em',
              textTransform: 'uppercase', color: 'text.secondary',
            }}>
              AI Asset Manager / Agent Workforce
            </Typography>
            <Chip label="preview" size="small" sx={{
              fontFamily: MONO, fontSize: '0.6rem', height: 17, color: 'text.disabled',
              bgcolor: 'transparent', border: '1px solid', borderColor: 'divider',
              '& .MuiChip-label': { px: 0.7 },
            }} />
          </Stack>
          <Typography component="h1" sx={{
            fontSize: { xs: '1.5rem', sm: '1.75rem' }, fontWeight: 700,
            letterSpacing: '-0.02em', color: 'text.primary',
          }}>
            Agent Workforce
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: '0.9rem', color: 'text.secondary', maxWidth: 580 }}>
            Six specialist workers. Each runs one agent per playbook you switch
            on — pick a worker, set how far it can act, and deploy the playbooks
            you want it handling.
          </Typography>
        </Box>

        {/* Live operational summary */}
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          <SummaryStat value={totals.totalRunning} label="agents running" live />
          <SummaryStat value={`${totals.activeFleets}/6`} label="workers active" />
          <SummaryStat value={totals.autopilot} label="on autopilot" />
        </Stack>
      </Stack>
    </Box>
  );
}

function SummaryStat({ value, label, live }) {
  return (
    <Box sx={{
      px: 1.75, py: 1.1, borderRadius: 1.5, minWidth: 96,
      border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper',
    }}>
      <Stack direction="row" alignItems="center" spacing={0.75}>
        {live && (
          <Box sx={{
            width: 7, height: 7, borderRadius: '50%', bgcolor: 'primary.main',
            boxShadow: `0 0 0 3px ${sig(0.15)}`, ...PULSE,
          }} />
        )}
        <Typography sx={{
          fontFamily: MONO, fontSize: '1.25rem', fontWeight: 600,
          color: 'text.primary', lineHeight: 1,
        }}>
          {value}
        </Typography>
      </Stack>
      <Typography sx={{ mt: 0.5, fontSize: '0.7rem', color: 'text.secondary' }}>
        {label}
      </Typography>
    </Box>
  );
}


// ── Fleet tab strip ─────────────────────────────────────────────────────────────
// One tab per worker. Each tab carries a live running-count indicator (the
// number of deployed playbooks): a filled Signal pill when agents are running,
// a muted "idle" chip when none are.
function FleetTabBar({ tab, onTab, counts }) {
  return (
    <Box sx={{
      border: '1px solid', borderColor: 'divider', borderRadius: 1.5,
      bgcolor: 'background.paper', mb: 2.5,
    }}>
      <Tabs
        value={tab}
        onChange={(_, v) => onTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          minHeight: 58,
          '& .MuiTabs-indicator': { height: 2 },
          '& .MuiTab-root': {
            minHeight: 58, textTransform: 'none', alignItems: 'center',
            px: 2, py: 0,
          },
        }}
      >
        {WORKERS.map((w) => (
          <Tab key={w.id} disableRipple
               label={<FleetTabLabel worker={w} count={counts[w.id]} active={WORKERS[tab].id === w.id} />} />
        ))}
      </Tabs>
    </Box>
  );
}

function FleetTabLabel({ worker, count, active }) {
  const Icon = worker.icon;
  return (
    <Stack direction="row" alignItems="center" spacing={1.1} sx={{ py: 0.5 }}>
      <Box sx={{
        width: 30, height: 30, borderRadius: '9px', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: active ? sig(0.1) : 'action.hover',
        border: '1px solid', borderColor: active ? sig(0.3) : 'transparent',
        transition: 'all 0.15s ease',
      }}>
        <Icon sx={{ fontSize: 17, color: active ? 'primary.main' : 'text.secondary' }} />
      </Box>
      <Box sx={{ textAlign: 'left' }}>
        <Typography sx={{
          fontSize: '0.84rem', fontWeight: active ? 700 : 600, lineHeight: 1.2,
          color: active ? 'text.primary' : 'text.secondary',
        }}>
          {worker.short}
        </Typography>
        <Typography sx={{ fontFamily: MONO, fontSize: '0.6rem', color: 'text.disabled', lineHeight: 1.3 }}>
          {worker.code}
        </Typography>
      </Box>
      <RunningBadge count={count} />
    </Stack>
  );
}

// The running indicator. >0 → live green pill with the count; 0 → muted "idle".
function RunningBadge({ count }) {
  if (count > 0) {
    return (
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{
        ml: 0.25, pl: 0.6, pr: 0.85, py: 0.3, borderRadius: 5,
        bgcolor: sig(0.12), border: '1px solid', borderColor: sig(0.35),
      }}>
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main', ...PULSE }} />
        <Typography sx={{ fontFamily: MONO, fontSize: '0.72rem', fontWeight: 700, color: 'primary.main', lineHeight: 1 }}>
          {count}
        </Typography>
      </Stack>
    );
  }
  return (
    <Box sx={{
      ml: 0.25, px: 0.85, py: 0.3, borderRadius: 5,
      border: '1px solid', borderColor: 'divider',
    }}>
      <Typography sx={{ fontFamily: MONO, fontSize: '0.66rem', color: 'text.disabled', lineHeight: 1.2 }}>
        idle
      </Typography>
    </Box>
  );
}


// ── Per-fleet console ────────────────────────────────────────────────────────────
function FleetConsole({ worker, deployedIdx, onToggleScenario, onDeployAll, onPauseAll, level, onLevel }) {
  const [facet, setFacet] = useState(0);
  const Icon = worker.icon;
  const running = deployedIdx.length;
  const isRunning = running > 0;

  return (
    <Box sx={{
      border: '1px solid', borderColor: 'divider', borderRadius: 2,
      bgcolor: 'background.paper', overflow: 'hidden',
    }}>
      {/* Identity band — worker + status, with the autonomy dial on the right */}
      <Box sx={{ p: { xs: 2.25, sm: 3 } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5}
               alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
          <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{
              width: 52, height: 52, borderRadius: '14px', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: sig(0.1), border: '1px solid', borderColor: sig(0.3),
            }}>
              <Icon sx={{ fontSize: 26, color: 'primary.main' }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={1.25} sx={{ flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '1.2rem', fontWeight: 700, letterSpacing: '-0.012em' }}>
                  {worker.name}
                </Typography>
                <FleetStatus isRunning={isRunning} count={running} total={worker.scenarios.length} />
              </Stack>
              <Typography sx={{ fontSize: '0.88rem', color: 'text.secondary', mt: 0.5, maxWidth: 620, lineHeight: 1.5 }}>
                {worker.mission}
              </Typography>
            </Box>
          </Stack>
          <Box sx={{ flexShrink: 0 }}>
            <AutonomyControl level={level} onLevel={onLevel} />
          </Box>
        </Stack>
      </Box>

      {/* Facet tabs */}
      <Box sx={{ px: { xs: 1, sm: 2 }, borderTop: '1px solid', borderColor: 'divider' }}>
        <Tabs value={facet} onChange={(_, v) => setFacet(v)} variant="scrollable" scrollButtons="auto" sx={{ minHeight: 44 }}>
          <Tab icon={<BoltOutlinedIcon sx={{ fontSize: 17 }} />} iconPosition="start" label="Playbooks" sx={{ minHeight: 44, fontSize: '0.82rem' }} />
          <Tab icon={<LanOutlinedIcon sx={{ fontSize: 17 }} />} iconPosition="start" label="Capabilities" sx={{ minHeight: 44, fontSize: '0.82rem' }} />
          <Tab icon={<ShieldOutlinedIcon sx={{ fontSize: 17 }} />} iconPosition="start" label="Guardrails" sx={{ minHeight: 44, fontSize: '0.82rem' }} />
          <Tab icon={<TimelineOutlinedIcon sx={{ fontSize: 17 }} />} iconPosition="start" label="Activity" sx={{ minHeight: 44, fontSize: '0.82rem' }} />
        </Tabs>
      </Box>

      {/* Facet body */}
      <Box sx={{ p: { xs: 2.25, sm: 3 } }}>
        {facet === 0 && (
          <ScenariosTab
            worker={worker} level={level}
            deployedIdx={deployedIdx} onToggleScenario={onToggleScenario}
            onDeployAll={onDeployAll} onPauseAll={onPauseAll}
          />
        )}
        {facet === 1 && <CapabilitiesTab worker={worker} />}
        {facet === 2 && <GuardrailsTab worker={worker} level={level} onLevel={onLevel} />}
        {facet === 3 && <ActivityTab worker={worker} running={running} />}
      </Box>
    </Box>
  );
}

function FleetStatus({ isRunning, count, total }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.6}>
      <Box sx={{
        width: 8, height: 8, borderRadius: '50%',
        bgcolor: isRunning ? 'primary.main' : 'text.disabled',
        boxShadow: isRunning ? `0 0 0 3px ${sig(0.15)}` : 'none',
        ...(isRunning ? PULSE : {}),
      }} />
      <Typography sx={{ fontSize: '0.76rem', fontWeight: 600, color: isRunning ? 'primary.main' : 'text.disabled' }}>
        {isRunning ? `${count} of ${total} agents running` : 'Idle — no playbooks deployed'}
      </Typography>
    </Stack>
  );
}

function AutonomyControl({ level, onLevel }) {
  const active = AUTONOMY.find((a) => a.id === level) || AUTONOMY[0];
  return (
    <Box>
      <Typography sx={{ fontFamily: MONO, fontSize: '0.62rem', letterSpacing: '0.06em',
                        textTransform: 'uppercase', color: 'text.disabled', mb: 0.6 }}>
        Autonomy · {active.label.toLowerCase()}
      </Typography>
      <Box sx={{
        display: 'inline-flex', p: 0.35, borderRadius: 1.5,
        bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
      }}>
        {AUTONOMY.map((a) => {
          const on = a.id === level;
          return (
            <Tooltip key={a.id} title={a.blurb} arrow enterDelay={300}>
              <Box
                onClick={() => onLevel(a.id)}
                sx={{
                  px: 1.4, py: 0.55, borderRadius: 1, cursor: 'pointer',
                  fontSize: '0.78rem', fontWeight: on ? 700 : 500,
                  color: on ? 'primary.contrastText' : 'text.secondary',
                  bgcolor: on ? 'primary.main' : 'transparent',
                  transition: 'all 0.15s ease',
                  '&:hover': { color: on ? 'primary.contrastText' : 'text.primary' },
                }}
              >
                {a.label}
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
}


// ── Facet: Playbooks ────────────────────────────────────────────────────────────
// Each playbook is a deployable agent. Switching one on adds an agent to this
// worker — which is exactly what the running count (here and on the tab) shows.
function ScenariosTab({ worker, level, deployedIdx, onToggleScenario, onDeployAll, onPauseAll }) {
  const total = worker.scenarios.length;
  const active = deployedIdx.length;
  return (
    <Stack spacing={1.5}>
      <Stack direction="row" alignItems="center" justifyContent="space-between"
             sx={{ flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
        <Typography sx={{ fontSize: '0.86rem', color: 'text.secondary', maxWidth: 560 }}>
          Each playbook runs as its own agent. Switch on the ones you want this
          worker handling — two deployed means two agents running here.
        </Typography>
        <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0 }}>
          <Button size="small" variant="text" disabled={active === total}
                  startIcon={<PlayArrowRoundedIcon sx={{ fontSize: 16 }} />}
                  onClick={onDeployAll}
                  sx={{ fontSize: '0.76rem', color: 'primary.main' }}>
            Deploy all
          </Button>
          <Button size="small" variant="text" disabled={active === 0}
                  startIcon={<PauseRoundedIcon sx={{ fontSize: 16 }} />}
                  onClick={onPauseAll}
                  sx={{ fontSize: '0.76rem', color: 'text.secondary' }}>
            Pause all
          </Button>
        </Stack>
      </Stack>
      {worker.scenarios.map((s, i) => (
        <ScenarioCard
          key={s.name} scenario={s} index={i + 1} level={level}
          active={deployedIdx.includes(i)} onToggle={() => onToggleScenario(i)}
        />
      ))}
    </Stack>
  );
}

function ScenarioCard({ scenario, index, level, active, onToggle }) {
  // A scenario's effective posture is gated by the dial: an "auto" scenario only
  // runs unattended on Autopilot, otherwise it falls back to needing approval.
  const effective =
    scenario.mode === 'auto' && level === 'autopilot' ? 'auto'
    : scenario.mode === 'auto' ? 'approve'
    : scenario.mode;
  const eff = MODE[effective];

  return (
    <Box sx={{
      border: '1px solid', borderRadius: 1.5, overflow: 'hidden',
      borderColor: active ? sig(0.35) : 'divider',
      transition: 'border-color 0.15s ease',
    }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between"
             sx={{ px: 2, py: 1.25, bgcolor: active ? sig(0.06) : 'action.hover' }}>
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
          <Typography sx={{ fontFamily: MONO, fontSize: '0.7rem', color: 'text.disabled' }}>
            {String(index).padStart(2, '0')}
          </Typography>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: 'text.primary' }}>
            {scenario.name}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ flexShrink: 0 }}>
          <ModeBadge mode={eff} />
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography sx={{
              fontFamily: MONO, fontSize: '0.66rem', fontWeight: 600,
              color: active ? 'primary.main' : 'text.disabled',
              display: { xs: 'none', sm: 'block' },
            }}>
              {active ? 'Deployed' : 'Deploy'}
            </Typography>
            <Switch checked={active} onChange={onToggle} size="small" color="primary" />
          </Stack>
        </Stack>
      </Stack>

      <Box sx={{ p: 2, opacity: active ? 1 : 0.55, transition: 'opacity 0.15s ease' }}>
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1.75 }}>
          <FieldLabel icon={BoltOutlinedIcon}>Trigger</FieldLabel>
          <Typography sx={{ fontSize: '0.86rem', color: 'text.primary', flex: 1 }}>
            {scenario.trigger}
          </Typography>
        </Stack>

        <FieldLabel icon={AutoAwesomeOutlinedIcon} block>How it reasons</FieldLabel>
        <Stack spacing={0.85} sx={{ mt: 1, mb: 1.75 }}>
          {scenario.steps.map((st, i) => (
            <Stack key={i} direction="row" spacing={1.25} alignItems="flex-start">
              <Box sx={{
                width: 18, height: 18, borderRadius: '50%', flexShrink: 0, mt: '1px',
                bgcolor: sig(0.1), color: 'primary.main',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: MONO, fontSize: '0.62rem', fontWeight: 700,
              }}>
                {i + 1}
              </Box>
              <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', lineHeight: 1.5 }}>
                {st}
              </Typography>
            </Stack>
          ))}
        </Stack>

        <Box sx={{ mt: 1, p: 1.5, borderRadius: 1.5, bgcolor: sig(0.05), border: '1px solid', borderColor: sig(0.18) }}>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <ArrowForwardRoundedIcon sx={{ fontSize: 16, color: 'primary.main', mt: '2px' }} />
            <Box>
              <Typography sx={{ fontFamily: MONO, fontSize: '0.62rem', letterSpacing: '0.06em',
                                textTransform: 'uppercase', color: 'primary.main', mb: 0.25 }}>
                Output
              </Typography>
              <Typography sx={{ fontSize: '0.86rem', color: 'text.primary' }}>
                {scenario.output}
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}

function FieldLabel({ icon: Icon, children, block }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0, minWidth: block ? 'auto' : 88 }}>
      <Icon sx={{ fontSize: 14, color: 'text.disabled' }} />
      <Typography sx={{ fontFamily: MONO, fontSize: '0.62rem', letterSpacing: '0.06em',
                        textTransform: 'uppercase', color: 'text.disabled' }}>
        {children}
      </Typography>
    </Stack>
  );
}

function ModeBadge({ mode }) {
  const map = {
    neutral: { color: 'text.secondary', border: 'divider',     bg: 'transparent' },
    warn:    { color: 'warning.main',   border: 'warning.main', bg: 'transparent' },
    signal:  { color: 'primary.main',   border: 'primary.main', bg: sig(0.08) },
  };
  const s = map[mode.tone];
  return (
    <Box sx={{ px: 1, py: 0.35, borderRadius: 1, flexShrink: 0, border: '1px solid', borderColor: s.border, bgcolor: s.bg }}>
      <Typography sx={{ fontFamily: MONO, fontSize: '0.64rem', letterSpacing: '0.03em', color: s.color, fontWeight: 600 }}>
        {mode.label}
      </Typography>
    </Box>
  );
}


// ── Facet: Capabilities ─────────────────────────────────────────────────────────
function CapabilitiesTab({ worker }) {
  return (
    <Stack spacing={3}>
      <Section title="Capabilities">
        <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
          {worker.capabilities.map((c) => (
            <Chip key={c} label={c} sx={{
              fontSize: '0.78rem', height: 28, bgcolor: 'action.hover',
              color: 'text.primary', border: '1px solid', borderColor: 'divider',
              '& .MuiChip-label': { px: 1.25 },
            }} />
          ))}
        </Stack>
      </Section>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
        <Section title="Data sources" icon={LanOutlinedIcon}>
          <Stack spacing={1}>
            {worker.dataSources.map((d) => (
              <Stack key={d} direction="row" alignItems="center" spacing={1.25}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main', flexShrink: 0 }} />
                <Typography sx={{ fontSize: '0.86rem', color: 'text.primary' }}>{d}</Typography>
              </Stack>
            ))}
          </Stack>
        </Section>

        <Section title="Actions it can take" icon={BoltOutlinedIcon}>
          <Stack spacing={1}>
            {worker.actions.map((a) => (
              <Stack key={a} direction="row" alignItems="center" spacing={1.25}>
                <NorthEastRoundedIcon sx={{ fontSize: 13, color: 'text.disabled', flexShrink: 0 }} />
                <Typography sx={{ fontSize: '0.86rem', color: 'text.primary' }}>{a}</Typography>
              </Stack>
            ))}
          </Stack>
        </Section>
      </Box>

      <Section title="Engine">
        <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
          {[`${worker.integrations} integrations`, 'Claude-powered reasoning', 'Every figure sourced', 'Full audit trail'].map((m) => (
            <Chip key={m} label={m} size="small" sx={{
              fontFamily: MONO, fontSize: '0.68rem', height: 24,
              bgcolor: 'transparent', color: 'text.secondary',
              border: '1px solid', borderColor: 'divider',
            }} />
          ))}
        </Stack>
      </Section>
    </Stack>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={0.6} sx={{ mb: 1.25 }}>
        {Icon && <Icon sx={{ fontSize: 15, color: 'text.disabled' }} />}
        <Typography sx={{ fontFamily: MONO, fontSize: '0.66rem', letterSpacing: '0.07em',
                          textTransform: 'uppercase', color: 'text.disabled' }}>
          {title}
        </Typography>
      </Stack>
      {children}
    </Box>
  );
}


// ── Facet: Guardrails ─────────────────────────────────────────────────────────
function GuardrailsTab({ worker, level, onLevel }) {
  const [threshold, setThreshold] = useState(25);
  return (
    <Stack spacing={3}>
      <Section title="Autonomy posture">
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.25 }}>
          {AUTONOMY.map((a) => {
            const on = a.id === level;
            return (
              <Box key={a.id} onClick={() => onLevel(a.id)} sx={{
                p: 1.75, borderRadius: 1.5, cursor: 'pointer',
                border: '1px solid', borderColor: on ? 'primary.main' : 'divider',
                bgcolor: on ? sig(0.06) : 'transparent',
                transition: 'all 0.15s ease',
                '&:hover': { borderColor: on ? 'primary.main' : sig(0.4) },
              }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                  <Box sx={{
                    width: 16, height: 16, borderRadius: '50%',
                    border: '2px solid', borderColor: on ? 'primary.main' : 'divider',
                    bgcolor: on ? 'primary.main' : 'transparent',
                    boxShadow: on ? 'inset 0 0 0 2px #fff' : 'none',
                  }} />
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: on ? 'primary.main' : 'text.primary' }}>
                    {a.label}
                  </Typography>
                </Stack>
                <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', lineHeight: 1.5 }}>
                  {a.blurb}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Section>

      <Section title="Unattended action limit">
        <Typography sx={{ fontSize: '0.84rem', color: 'text.secondary', mb: 2 }}>
          On Autopilot, {worker.short} acts without sign-off only when the decision
          value is below this limit. Anything larger is escalated for approval.
        </Typography>
        <Box sx={{ px: 1, maxWidth: 460 }}>
          <Slider
            value={threshold}
            onChange={(_, v) => setThreshold(v)}
            min={0} max={100} step={5}
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => `$${v}k`}
            marks={[{ value: 0, label: '$0' }, { value: 50, label: '$50k' }, { value: 100, label: '$100k+' }]}
            sx={{ color: 'primary.main' }}
          />
        </Box>
      </Section>

      <Section title="Standing guardrails">
        <Stack spacing={1}>
          {[
            { icon: VerifiedUserOutlinedIcon, t: 'Every action is logged and traceable to its source data' },
            { icon: ShieldOutlinedIcon,       t: 'Counterparty-facing sends always require human sign-off' },
            { icon: ScheduleOutlinedIcon,     t: 'Escalates to the named owner when confidence is low or data is stale' },
            { icon: LanOutlinedIcon,          t: 'Operates only on the data sources you connect — nothing else' },
          ].map((g) => (
            <Stack key={g.t} direction="row" spacing={1.25} alignItems="center" sx={{
              p: 1.25, borderRadius: 1.5, border: '1px solid', borderColor: 'divider',
            }}>
              <g.icon sx={{ fontSize: 18, color: 'primary.main', flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.85rem', color: 'text.primary' }}>{g.t}</Typography>
            </Stack>
          ))}
        </Stack>
      </Section>
    </Stack>
  );
}


// ── Facet: Activity ─────────────────────────────────────────────────────────────
function ActivityTab({ worker, running }) {
  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Box sx={{
          width: 8, height: 8, borderRadius: '50%',
          bgcolor: running > 0 ? 'primary.main' : 'text.disabled',
          boxShadow: running > 0 ? `0 0 0 4px ${sig(0.15)}` : 'none',
          ...(running > 0 ? PULSE : {}),
        }} />
        <Typography sx={{ fontFamily: MONO, fontSize: '0.7rem', letterSpacing: '0.06em',
                          textTransform: 'uppercase', color: 'text.secondary' }}>
          {running > 0 ? `${running} running — illustrative activity` : 'Fleet idle — no workers running'}
        </Typography>
      </Stack>

      {running === 0 ? (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography sx={{ fontSize: '0.88rem', color: 'text.disabled' }}>
            Deploy a worker to start this fleet. Recent actions will appear here.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ position: 'relative', pl: 2.5 }}>
          <Box sx={{ position: 'absolute', left: 5, top: 6, bottom: 6, width: '1px', bgcolor: 'divider' }} />
          <Stack spacing={2}>
            {worker.feed.map((f, i) => (
              <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start" sx={{ position: 'relative' }}>
                <Box sx={{
                  position: 'absolute', left: -20, top: 4,
                  width: 11, height: 11, borderRadius: '50%', bgcolor: 'background.paper',
                  border: '2px solid',
                  borderColor: f.tone === 'warn' ? 'warning.main' : f.tone === 'ok' ? 'primary.main' : 'text.disabled',
                }} />
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: '0.88rem', color: 'text.primary', lineHeight: 1.45 }}>
                    {f.text}
                  </Typography>
                  <Typography sx={{ fontFamily: MONO, fontSize: '0.68rem', color: 'text.disabled', mt: 0.25 }}>
                    {f.t}
                  </Typography>
                </Box>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
}
